import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspaceResolver } from './useWorkspaceResolver';
import { useBusinessUnitContext } from '@/contexts/useBusinessUnitContext';
import { startOfWeek, endOfWeek, startOfDay, endOfDay, addDays, format } from 'date-fns';
import { useAuth } from '@/lib/auth';
import { logger } from '@/lib/logger';
import { handleError } from '@/lib/errorHandler';
import { applyWorkspaceScope } from '@/lib/workspaceScope';

export interface CalendarEvent {
  id: string;
  type: 'followup' | 'meeting' | 'deal_milestone' | 'focus_block';
  title: string;
  start: Date;
  end?: Date;
  leadId?: string;
  leadName?: string;
  leadTemperature?: string;
  actionType?: string;
  isOverdue: boolean;
  priority?: 'normal' | 'important' | 'urgent';
  status?: 'scheduled' | 'done' | 'canceled';
  meta?: Record<string, any>;
}

import { useWorkspaceResolver } from './useWorkspaceResolver';
import { EventsService } from '@/data/events.service';
import { LeadsService } from '@/data/leads.service';
import { DealsService } from '@/data/deals.service';

export function useCalendarEvents(viewDate: Date, view: 'week' | 'day') {
  const { workspaceId, isLoading: workspaceLoading } = useWorkspaceResolver();
  const { scope, isLoading: scopeLoading } = useBusinessUnitContext();

  const start = view === 'week' 
    ? startOfWeek(viewDate, { weekStartsOn: 1 }) 
    : startOfDay(viewDate);
  const end = view === 'week' 
    ? endOfWeek(viewDate, { weekStartsOn: 1 }) 
    : endOfDay(viewDate);

  return useQuery({
    queryKey: ['calendar-events', scope, format(start, 'yyyy-MM-dd'), view],
    queryFn: async (): Promise<CalendarEvent[]> => {
      if (scope.type === 'all' && scope.workspaceIds.length === 0) return [];
      if (scope.type === 'single' && !scope.workspaceId) return [];

      const events: CalendarEvent[] = [];
      const now = new Date();

      // 1. Fetch scheduled follow-ups from leads (decision-first: use next_follow_up_at)
      const leadsWithFollowUps = await LeadsService.getLeadsWithUpcomingActions(scope, start.toISOString(), end.toISOString());

      leadsWithFollowUps.forEach(lead => {
        // Use next_follow_up_at (decision-first) or fallback to next_action_at
        const followUpDate = (lead as any).next_follow_up_at 
          ? new Date((lead as any).next_follow_up_at) 
          : (lead.next_action_at ? new Date(lead.next_action_at) : null);
        
        if (followUpDate) {
          events.push({
            id: `followup-${lead.id}`,
            type: 'followup',
            title: `Follow up: ${lead.name}`,
            start: followUpDate,
            leadId: lead.id,
            leadName: lead.name,
            leadTemperature: lead.temperature || undefined,
            actionType: lead.next_action_type || 'call',
            isOverdue: followUpDate < now,
          });
        }
      });

      // 2. Fetch meetings/events from events table
      const meetingEvents = await EventsService.getEvents(scope, start.toISOString(), end.toISOString());
      
      // Fetch leads separately if needed
      const eventLeadIds = meetingEvents.map(e => e.lead_id).filter(Boolean) as string[];
      let eventLeadsMap = new Map<string, any>();
      
      if (eventLeadIds.length > 0) {
        const eventLeadsData = await LeadsService.getLeadsByIds(scope, eventLeadIds);
        if (eventLeadsData) {
          eventLeadsData.forEach(lead => {
            eventLeadsMap.set(lead.id, lead);
          });
        }
      }
      
      meetingEvents.forEach(event => {
        const isScheduled = event.status !== 'done' && event.status !== 'canceled';
        const lead = event.lead_id ? eventLeadsMap.get(event.lead_id) : null;
        events.push({
          id: `event-${event.id}`,
          type: 'meeting',
          title: event.title,
          start: new Date(event.start_at),
          end: event.end_at ? new Date(event.end_at) : undefined,
          leadId: lead?.id,
          leadName: lead?.name,
          leadTemperature: lead?.temperature || undefined,
          actionType: event.type,
          isOverdue: new Date(event.start_at) < now && isScheduled,
          priority: event.priority || 'normal',
          status: event.status || 'scheduled',
          meta: { notes: event.notes },
        });
      });

      // 3. Fetch focus sessions
      let focusSessionsQuery = supabase
        .from('focus_sessions')
        .select('*')
        .gte('started_at', start.toISOString())
        .lte('started_at', end.toISOString());
      
      focusSessionsQuery = applyWorkspaceScope(focusSessionsQuery, scope);
      
      const { data: focusSessionsData, error: focusSessionsError } = await focusSessionsQuery;

      if (focusSessionsError) {
        console.error('[useCalendarEvents] Error fetching focus sessions:', focusSessionsError);
      }
      
      const focusSessions = (focusSessionsData || []) as Array<any>;

      focusSessions?.forEach(session => {
        const startDate = new Date(session.started_at);
        const endDate = session.ended_at 
          ? new Date(session.ended_at) 
          : new Date(startDate.getTime() + session.duration_minutes * 60000);
        
        events.push({
          id: `focus-${session.id}`,
          type: 'focus_block',
          title: 'Focus Block',
          start: startDate,
          end: endDate,
          isOverdue: false,
          meta: { 
            actionsCompleted: session.actions_completed,
            followupsScheduled: session.followups_scheduled,
          },
        });
      });

      // 4. Fetch deal milestones (expected_close_date)
      const dealsWithCloseDate = await DealsService.getDeals(scope, {
        expectedCloseDateFrom: format(start, 'yyyy-MM-dd'),
        expectedCloseDateTo: format(end, 'yyyy-MM-dd'),
      }).then(deals => deals.filter(d => d.expected_close_date));
      
      // Fetch leads for deals separately
      const dealLeadIds = dealsWithCloseDate.map(d => d.lead_id).filter(Boolean) as string[];
      let dealLeadsMap = new Map<string, any>();
      
      if (dealLeadIds.length > 0) {
        const dealLeadsData = await LeadsService.getLeadsByIds(scope, dealLeadIds);
        if (dealLeadsData) {
          dealLeadsData.forEach(lead => {
            dealLeadsMap.set(lead.id, lead);
          });
        }
      }
      
      dealsWithCloseDate.forEach(deal => {
        if (deal.expected_close_date) {
          const closeDate = new Date(deal.expected_close_date);
          const lead = deal.lead_id ? dealLeadsMap.get(deal.lead_id) : null;
          events.push({
            id: `deal-${deal.id}`,
            type: 'deal_milestone',
            title: `Close: ${lead?.name || 'Deal'}`,
            start: closeDate,
            leadId: lead?.id,
            leadName: lead?.name,
            leadTemperature: lead?.temperature || undefined,
            isOverdue: closeDate < now && deal.stage !== 'won' && deal.stage !== 'lost',
            meta: { 
              dealValue: deal.value,
              stage: deal.stage,
            },
          });
        }
      });

      // Sort by start time
      return events.sort((a, b) => a.start.getTime() - b.start.getTime());
    },
    enabled: !workspaceLoading && !scopeLoading && (scope.type === 'all' ? scope.workspaceIds.length > 0 : !!scope.workspaceId),
  });
}

export function useCreateEvent() {
  const queryClient = useQueryClient();
  const { workspaceId } = useWorkspaceResolver();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (event: {
      title: string;
      type: 'call' | 'demo' | 'meeting' | 'fair' | 'other';
      start_at: string;
      end_at?: string;
      lead_id?: string;
      notes?: string;
      priority?: 'normal' | 'important' | 'urgent';
      reminder_offsets_minutes?: number[];
    }) => {
      if (!workspaceId) {
        throw new Error('No workspace available. Please ensure you are logged in and have an active workspace.');
      }

      logger.mutation('createEvent', { workspaceId, userId: user?.id, eventType: event.type });
      return EventsService.createEvent({ workspace_id: workspaceId, ...event });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['reminder-events', workspaceId] });
    },
    onError: (error) => {
      handleError(error, { action: 'Create Event', component: 'useCreateEvent', workspaceId });
    },
  });
}

export function useUpdateLeadFollowup() {
  const queryClient = useQueryClient();
  const { workspaceId } = useWorkspaceResolver();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ 
      leadId, 
      nextActionAt, 
      nextActionType 
    }: { 
      leadId: string; 
      nextActionAt: string | null;
      nextActionType: string | null;
    }) => {
      if (!workspaceId) throw new Error('No workspace available');
      logger.mutation('updateLeadFollowup', { workspaceId, userId: user?.id, leadId });
      return LeadsService.updateLead({ 
        id: leadId, 
        workspace_id: workspaceId, 
        next_action_at: nextActionAt, 
        next_action_type: nextActionType as any 
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['leads', workspaceId] });
    },
    onError: (error) => {
      handleError(error, { action: 'Update Lead Followup', component: 'useUpdateLeadFollowup', workspaceId });
    },
  });
}

export function useUpdateEvent() {
  const queryClient = useQueryClient();
  const { workspaceId } = useWorkspaceResolver();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ 
      eventId, 
      updates 
    }: { 
      eventId: string; 
      updates: {
        title?: string;
        type?: 'call' | 'demo' | 'meeting' | 'fair' | 'other';
        start_at?: string;
        end_at?: string;
        lead_id?: string | null;
        notes?: string;
        priority?: 'normal' | 'important' | 'urgent';
        status?: 'scheduled' | 'done' | 'canceled';
      };
    }) => {
      if (!workspaceId) throw new Error('No workspace available');
      logger.mutation('updateEvent', { workspaceId, userId: user?.id, eventId });
      return EventsService.updateEvent({ id: eventId, workspace_id: workspaceId, ...updates });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['reminder-events', workspaceId] });
    },
    onError: (error) => {
      handleError(error, { action: 'Update Event', component: 'useUpdateEvent', workspaceId });
    },
  });
}

export function useDeleteEvent() {
  const queryClient = useQueryClient();
  const { workspaceId } = useWorkspaceResolver();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({ eventId, leadId, isFollowUp }: { eventId: string; leadId?: string; isFollowUp?: boolean }) => {
      if (!workspaceId) throw new Error('No workspace available');
      logger.mutation('deleteEvent', { workspaceId, userId: user?.id, eventId, leadId, isFollowUp });
      
      // Delete event (only if it's a real event, not a follow-up from leads table)
      if (!isFollowUp) {
        await EventsService.deleteEvent(eventId, workspaceId);
      }
      
      // If it's a follow-up event, remove next_follow_up_at from lead
      if (isFollowUp && leadId) {
        const { LeadsService } = await import('@/data/leads.service');
        await LeadsService.updateLead({
          id: leadId,
          workspace_id: workspaceId,
          next_follow_up_at: null,
        } as any);
        logger.info('[useDeleteEvent] Removed next_follow_up_at from lead', { leadId, workspaceId });
      }
    },
    onSuccess: (_, { leadId, isFollowUp }) => {
      queryClient.invalidateQueries({ queryKey: ['calendar-events', workspaceId] });
      queryClient.invalidateQueries({ queryKey: ['reminder-events', workspaceId] });
      if (isFollowUp && leadId) {
        queryClient.invalidateQueries({ queryKey: ['leads', workspaceId] });
      }
    },
    onError: (error) => {
      handleError(error, { action: 'Delete Event', component: 'useDeleteEvent', workspaceId });
    },
  });
}
